import { useEffect, useState } from "react";
import { T, UI, FONT } from "../constants/tokens";
import { isLessonShown, openAcademyLesson } from "../utils/academyNav";
import { Rich, Sources } from "./AcademyText";

// ─── "Concept of the week" ───────────────────────────────────────────────────
// One term from the Academy glossary (academy/glossary.md, 70 terms), rotating
// by ISO week so everyone sees the same term in a given week. The term keeps
// its own source, shown as links. Rendered only while the Academy is on
// (AIAnalysisTab, constants/features.js). The eight hand-written concepts it
// used to carry had no sources and are gone (sprint 12, stage 3).
//
// The glossary comes with the Academy's content, loaded on demand (sprint 13,
// 6.2): the card appears when it arrives, and the content stays out of the
// main bundle.
export default function ConceptOfWeekCard() {
  const [term, setTerm] = useState(null);
  useEffect(() => {
    let alive = true;
    import("../utils/academy").then(m => { if (alive) setTerm(m.termOfWeek() || null); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!term) return null;
  // The lesson the term comes from, when it is shown (sprint 13, 2.1).
  const lessonId = (term.lessons || []).find(isLessonShown) || null;
  return (
    <div data-concept={term.n} style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "16px 18px" }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: UI.text.primary, fontFamily: FONT.heading }}>מושג השבוע</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: UI.text.primary, marginTop: 8 }}><Rich text={term.term}/></div>
      <p style={{ margin: "8px 0 0", fontSize: 15, color: UI.text.secondary, lineHeight: 1.7 }}><Rich text={term.text}/></p>
      {term.tax && <p style={{ margin: "6px 0 0", fontSize: 14, fontWeight: 700, color: UI.text.primary }}>לבירור מצבך האישי – יועץ מס.</p>}
      <div style={{ marginTop: 12 }}>
        <Sources sources={term.sources} title="מקור"/>
      </div>
      <button
        onClick={() => openAcademyLesson(lessonId)}
        style={{ marginTop: 12, background: UI.fill.subtle, border: `1px solid ${UI.line.strong}`, borderRadius: T.radius.md, padding: "8px 16px", minHeight: 40, fontSize: 13, fontWeight: 700, color: UI.text.primary, cursor: "pointer", fontFamily: "inherit" }}>
        {lessonId ? <>לשיעור <bdi dir="ltr">{lessonId}</bdi> באקדמיה</> : "לאקדמיה"}
      </button>
    </div>
  );
}

// ─── One line on the home screen (sprint 19, stage 4) ───────────────────────
// "מושג השבוע: <מושג> ←" · the term only. A tap opens its lesson in the
// Academy, where the explanation and the source are; with no lesson shown,
// the Academy itself.
export function ConceptOfWeekLine({ style }) {
  const [term, setTerm] = useState(null);
  useEffect(() => {
    let alive = true;
    import("../utils/academy").then(m => { if (alive) setTerm(m.termOfWeek() || null); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!term) return null;
  const lessonId = (term.lessons || []).find(isLessonShown) || null;
  return (
    <button type="button" data-concept-line={term.n} onClick={() => openAcademyLesson(lessonId)}
      style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%", minHeight: 48, padding: "10px 16px",
        background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl,
        fontFamily: "inherit", fontSize: 14.5, color: UI.text.primary, cursor: "pointer", textAlign: "start", ...style,
      }}>
      <span style={{ fontWeight: 800 }}>מושג השבוע:</span>
      <span style={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><Rich text={term.term}/></span>
      <span aria-hidden style={{ fontSize: 16, lineHeight: 1, color: UI.interactive }}>←</span>
    </button>
  );
}
